import type { FC, HTMLAttributes, ReactNode } from "react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

const tagVariants = cva(
  "inline-flex items-center justify-center gap-8 rounded-[1000px] border border-neutral-light-grey bg-neutral-black/5",
  {
    variants: {
      size: {
        l: "px-16 py-8 text-16",
        m: "px-16 py-8 text-14",
        /** Inside a field, where the tag sits in the input's own padding (Figma 931:54123). */
        s: "px-8 py-4 text-16",
      },
    },
    defaultVariants: {
      size: "l",
    },
  },
);

const tagIconSize = {
  l: "size-20",
  m: "size-18",
  s: "size-20",
} as const;

type TagProps = HTMLAttributes<HTMLSpanElement> &
  VariantProps<typeof tagVariants> & {
    iconLeft?: boolean;
    iconRight?: boolean;
    children?: ReactNode;
  };

export const Tag: FC<TagProps> = ({ className, size = "l", iconLeft, iconRight = true, children, ...props }) => (
  <span className={cn(tagVariants({ size }), className)} {...props}>
    {iconLeft && <XMarkIcon className={cn(tagIconSize[size ?? "l"], "text-neutral-black shrink-0")} />}
    {children}
    {iconRight && <XMarkIcon className={cn(tagIconSize[size ?? "l"], "text-neutral-black shrink-0")} />}
  </span>
);
